'use client';

import { useTranslations } from 'next-intl';
import { Text } from '@cobrastyle/ui';
import type { FreeGiftLine } from '@/lib/free-gift/types';

/** "Ingår" marker for a gift line. */
export function FreeGiftBadge({ className = '' }: { className?: string }) {
  const t = useTranslations('cart');

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 bg-primary-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white ${className}`}
    >
      <svg
        className="h-2.5 w-2.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v9H4v-9M2 7h20v5H2zM12 7v14" />
      </svg>
      {t('freeGift.badge')}
    </span>
  );
}

/**
 * Warning that part of a gift line is being charged, which happens when the
 * customer lowers or removes the qualifying product — the server reverts the
 * excess units to full price. Shown rather than silently deleting the line, so
 * the customer decides what to do with it.
 */
export function FreeGiftChargeWarning({
  line,
  className = '',
}: {
  line: FreeGiftLine;
  className?: string;
}) {
  const t = useTranslations('cart');

  if (line.paidUnits <= 0) return null;

  return (
    <Text size="sm" className={`text-warning ${className}`}>
      {t('freeGift.partiallyCharged', { count: line.paidUnits })}
    </Text>
  );
}

/**
 * Read-only quantity for a gift line. The gift's quantity follows the qualifying
 * product, so editing it here would only risk charging for extra units.
 */
export function FreeGiftQuantity({
  quantity,
  className = '',
}: {
  quantity: number;
  className?: string;
}) {
  const t = useTranslations('cart');

  return (
    <Text size="sm" variant="muted" className={className}>
      {t('freeGift.quantity', { count: quantity })}
    </Text>
  );
}
